import type React from 'react';
import type { ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import type { OverrideProps } from '#/ui/utils/types';

export type DividerVariant = 'fullWidth' | 'inset' | 'middle';
export type DividerOrientation = 'horizontal' | 'vertical';
export type DividerTextAlign = 'center' | 'left' | 'right';

export interface DividerOwnProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * The component orientation.
   * @default 'horizontal'
   */
  orientation?: DividerOrientation;
  /**
   * The text alignment when there's content.
   * @default 'center'
   */
  textAlign?: DividerTextAlign;
  /**
   * The variant to use.
   * @default 'fullWidth'
   */
  variant?: DividerVariant;
  /**
   * If true, a margin is added to accommodate for a nearby flex container.
   * @default false
   */
  flexItem?: boolean;
}

export type DividerTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'div',
> = {
  props: AdditionalProps & DividerOwnProps;
  defaultComponent: RootComponent;
};

export type DividerProps<
  RootComponent extends React.ElementType = DividerTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<DividerTypeMap<AdditionalProps, RootComponent>, RootComponent>;

const variantClasses: Record<DividerVariant, string> = {
  fullWidth: '',
  inset: 'ml-16',
  middle: 'mx-8',
};

const textAlignClasses: Record<DividerTextAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

function Divider<C extends ElementType = 'div'>({
  children,
  orientation = 'horizontal',
  textAlign = 'center',
  variant = 'fullWidth',
  flexItem = false,
  className,
  ...props
}: DividerProps<C>) {
  const isVertical = orientation === 'vertical';
  const hasText = Boolean(children);

  if (hasText && !isVertical) {
    return (
      <div
        className={twMerge(
          'flex items-center',
          textAlignClasses[textAlign],
          variantClasses[variant],
          flexItem && 'self-stretch',
          className
        )}
        {...props}
      >
        <div
          className={twMerge(
            'flex-grow border-t border-base-content/20',
            textAlign === 'left' && 'mr-4',
            (textAlign === 'center' || textAlign === 'right') && 'flex-grow'
          )}
        />
        <div className='px-4'>{children}</div>
        <div
          className={twMerge(
            'flex-grow border-t border-base-content/20',
            textAlign === 'right' && 'ml-4',
            (textAlign === 'center' || textAlign === 'left') && 'flex-grow'
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        isVertical ? 'inline-block h-auto border-l' : 'block w-auto border-t',
        'border-base-content/20',
        !isVertical && variantClasses[variant],
        flexItem && 'self-stretch shrink',
        className
      )}
      {...props}
    />
  );
}

Divider.displayName = 'Divider';

export default Divider;
